import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, AlertTriangle, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import useApi from "../utils/useApi";
import apis from "../utils/apis";
import { clearLocalStorageExcept } from "../utils/utils";
import { OtpPurpose } from "../utils/OtpPurpose";
import { resetReduxStore, clearUserStorage } from "../utils/reduxUtils";

const DeleteAccountModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.user?.userInfo);

  const [otp, setOtp] = useState("");
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [deleteUserToken, setDeleteUserToken] = useState("");
  const [otpTimeoutError, setOtpTimeoutError] = useState(false);
  const [timeoutCooldown, setTimeoutCooldown] = useState(0);

  const { callApi: callSendOtp, isLoading: sendOtpLoading } = useApi();
  const { callApi: callVerifyOtp, isLoading: verifyOtpLoading } = useApi();
  const { callApi: callDeleteAccount, isLoading: deleteAccountLoading } =
    useApi();

  // Initialize timeout from localStorage on component mount
  useEffect(() => {
    if (isOpen) {
      const storedTimeoutEnd = localStorage.getItem(
        "deleteAccountOtpTimeoutEnd"
      );
      if (storedTimeoutEnd) {
        const endTime = parseInt(storedTimeoutEnd, 10);
        const now = Date.now();
        const remainingTime = Math.floor((endTime - now) / 1000);

        if (remainingTime > 0) {
          setTimeoutCooldown(remainingTime);
          setOtpTimeoutError(true);
        } else {
          localStorage.removeItem("deleteAccountOtpTimeoutEnd");
        }
      }
    }
  }, [isOpen]);

  const handleSendOtp = async () => {
    if (resendCooldown > 0 || timeoutCooldown > 0) {
      return;
    }

    try {
      const res = await callSendOtp({
        api: apis.sendOtp,
        body: { mobile: user?.mobile, purpose: OtpPurpose.ACCOUNT_DELETE },
      });

      if (res?.code === "OTP_TIMOUT") {
        setOtpTimeoutError(true);
        setTimeoutCooldown(60);
        const timeoutEndTime = Date.now() + 60 * 1000;
        localStorage.setItem(
          "deleteAccountOtpTimeoutEnd",
          timeoutEndTime.toString()
        );
        setShowOtpInput(true);
        return;
      }

      setShowOtpInput(true);
      setResendCooldown(60);
      setOtpTimeoutError(false);
      setTimeoutCooldown(0);
      localStorage.removeItem("deleteAccountOtpTimeoutEnd");
      toast.success(t("deleteAccount.otpSent"));
    } catch (error) {
      if (error?.code === "OTP_TIMOUT") {
        setOtpTimeoutError(true);
        setTimeoutCooldown(60);
        setShowOtpInput(true);
        const timeoutEndTime = Date.now() + 60 * 1000;
        localStorage.setItem(
          "deleteAccountOtpTimeoutEnd",
          timeoutEndTime.toString()
        );
        toast.error(t("login.errors.otpTimeout.toast"));
      } else {
        toast.error(t("deleteAccount.otpSendError"));
      }
      console.error("Failed to send OTP:", error);
    }
  };

  const handleVerifyOtp = async () => {
    try {
      if (!otp || otp.length !== 6) {
        toast.error(t("deleteAccount.invalidOtp"));
        return;
      }

      const res = await callVerifyOtp({
        api: apis.verifyOtp,
        body: { mobile: user?.mobile, otp: otp },
      });

      const token = res?.token;
      setDeleteUserToken(token);

      // Clear OTP and close modal
      setOtp("");
      setShowOtpInput(false);
      setShowConfirmModal(true);
      toast.success(t("deleteAccount.otpVerified"));
    } catch (error) {
      toast.error(t("deleteAccount.otpVerifyError"));
      console.error("OTP verification failed:", error);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await callDeleteAccount({
        api: apis.deleteAccount,
        customToken: deleteUserToken,
      });

      setShowConfirmModal(false);
      onClose();
      toast.success(t("deleteAccount.deleteSuccess"));

      // Get current user ID before clearing
      const userId = user?.id || user?._id || "guest";

      // Clear user-specific Redux state storage
      clearUserStorage(userId);

      // Reset all Redux slices to initial state
      resetReduxStore(dispatch);

      // Clear other localStorage except language and firstLogin
      clearLocalStorageExcept({ values: ["language", "firstLogin"] });

      navigate("/login");
    } catch (error) {
      toast.error(t("deleteAccount.deleteError"));
      console.error("Failed to delete account:", error);
    }
  };

  const handleResendOtp = async () => {
    setOtp("");
    await handleSendOtp();
  };

  const handleClose = () => {
    setShowOtpInput(false);
    setShowConfirmModal(false);
    setOtp("");
    onClose();
  };

  // Resend cooldown timer
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Timeout cooldown timer
  useEffect(() => {
    let timer;
    if (timeoutCooldown > 0) {
      timer = setTimeout(() => setTimeoutCooldown(timeoutCooldown - 1), 1000);
    } else if (timeoutCooldown === 0 && otpTimeoutError) {
      setOtpTimeoutError(false);
      localStorage.removeItem("deleteAccountOtpTimeoutEnd");
    }
    return () => clearTimeout(timer);
  }, [timeoutCooldown, otpTimeoutError]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {/* Warning Modal */}
      {!showOtpInput && !showConfirmModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-dark-bg rounded-lg shadow-xl p-6 w-full max-w-md section relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-light-gray-400 dark:text-dark-gray-400 hover:text-light-foreground dark:hover:text-dark-foreground"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center">
              <div className="mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full p-4 w-20 h-20 flex items-center justify-center">
                <AlertTriangle className="text-red-600 dark:text-red-400 w-10 h-10" />
              </div>
              <h2 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-2">
                {t("deleteAccount.warning")}
              </h2>
              <p className="text-light-gray-600 dark:text-dark-gray-400 mb-6">
                {t("deleteAccount.description")}
              </p>

              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 text-left">
                <h3 className="font-semibold text-red-800 dark:text-red-400 mb-2">
                  {t("deleteAccount.warningTitle")}
                </h3>
                <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
                  <li>• {t("deleteAccount.warning1")}</li>
                  <li>• {t("deleteAccount.warning2")}</li>
                  <li>• {t("deleteAccount.warning3")}</li>
                </ul>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleSendOtp}
                  disabled={sendOtpLoading}
                  className="w-full bg-red-600 dark:bg-red-700 text-white py-3 rounded-lg font-semibold hover:bg-red-700 dark:hover:bg-red-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendOtpLoading
                    ? t("deleteAccount.sendingOtp")
                    : t("deleteAccount.proceedToDelete")}
                </button>
                <button
                  onClick={handleClose}
                  className="w-full bg-light-gray-200 dark:bg-dark-gray-700 text-light-foreground dark:text-dark-foreground py-3 rounded-lg font-semibold hover:bg-light-gray-300 dark:hover:bg-dark-gray-600 transition-colors"
                >
                  {t("deleteAccount.cancel")}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* OTP Input Modal */}
      {showOtpInput && !showConfirmModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={handleClose}
        >
          <motion.div
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            className="bg-white dark:bg-dark-bg rounded-lg shadow-xl p-6 w-full max-w-md section relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-light-gray-400 dark:text-dark-gray-400 hover:text-light-foreground dark:hover:text-dark-foreground"
            >
              <X className="w-6 h-6" />
            </button>

            <h2 className="text-2xl font-semibold mb-4 text-light-foreground dark:text-dark-foreground">
              {t("deleteAccount.enterOtp")}
            </h2>

            {/* Timeout Warning */}
            {otpTimeoutError && timeoutCooldown > 0 && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-600 p-4 mb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-yellow-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium">
                      {t("login.errors.otpTimeout.cannotResend", {
                        minutes: Math.floor(timeoutCooldown / 60),
                        seconds: (timeoutCooldown % 60)
                          .toString()
                          .padStart(2, "0"),
                      })}
                    </p>
                    <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                      {t("login.errors.otpTimeout.enterPreviousOtp")}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label
                  htmlFor="otp"
                  className="text-sm font-medium text-light-foreground dark:text-dark-foreground mb-1 block"
                >
                  {t("deleteAccount.otpLabel")}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    placeholder={t("deleteAccount.otpPlaceholder")}
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-light-border dark:border-dark-border focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-light-background dark:bg-dark-background text-light-foreground dark:text-dark-foreground placeholder-light-gray-400 dark:placeholder-dark-gray-500"
                    maxLength={6}
                  />
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-light-gray-400 dark:text-dark-gray-400 w-5 h-5" />
                </div>
              </div>

              <button
                onClick={handleVerifyOtp}
                disabled={verifyOtpLoading || otp.length !== 6}
                className="w-full bg-red-600 dark:bg-red-700 text-white py-2 rounded-lg font-semibold hover:bg-red-700 dark:hover:bg-red-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verifyOtpLoading
                  ? t("deleteAccount.verifying")
                  : t("deleteAccount.verifyOtp")}
              </button>

              <p className="text-sm text-light-gray-600 dark:text-dark-gray-400 text-center">
                {resendCooldown > 0 || timeoutCooldown > 0 ? (
                  <span>
                    {t("login.resendIn", {
                      count: Math.max(resendCooldown, timeoutCooldown),
                    })}
                  </span>
                ) : (
                  <button
                    onClick={handleResendOtp}
                    className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                  >
                    {t("deleteAccount.resendOtp")}
                  </button>
                )}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Confirm Delete Modal */}
      {showConfirmModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-dark-bg rounded-lg shadow-xl p-6 w-full max-w-md section"
          >
            <div className="text-center">
              <div className="mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full p-4 w-20 h-20 flex items-center justify-center">
                <Trash2 className="text-red-600 dark:text-red-400 w-10 h-10" />
              </div>
              <h2 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">
                {t("deleteAccount.confirmTitle")}
              </h2>
              <p className="text-light-foreground dark:text-dark-foreground mb-6">
                {t("deleteAccount.confirmMessage")}
              </p>
              <div className="space-y-3">
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteAccountLoading}
                  className="w-full bg-red-600 dark:bg-red-700 text-white py-3 rounded-lg font-semibold hover:bg-red-700 dark:hover:bg-red-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteAccountLoading
                    ? t("deleteAccount.deleting")
                    : t("deleteAccount.confirmDelete")}
                </button>
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    handleClose();
                  }}
                  className="w-full bg-light-gray-200 dark:bg-dark-gray-700 text-light-foreground dark:text-dark-foreground py-3 rounded-lg font-semibold hover:bg-light-gray-300 dark:hover:bg-dark-gray-600 transition-colors"
                >
                  {t("deleteAccount.cancel")}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DeleteAccountModal;
